import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertOctagon, ChevronRight, ExternalLink, X, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '—';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/**
 * CronFailureAlert — global header red badge that surfaces critical cron failures.
 * Polls /api/sync/cron-alerts every 60s. Shown only when one or more critical jobs:
 *   - Have failed 2+ times in a row, OR
 *   - Are 'daily' and have had no successful run in 24h+
 * Persists until the underlying job succeeds (backend-driven, no client snooze).
 * Click → opens dropdown listing failing jobs; each row links to
 * /sync-center?tab=schedules&highlight=<job_id>.
 */
export default function CronFailureAlert() {
  const [alerts, setAlerts] = useState([]);
  const [freshnessAlerts, setFreshnessAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchAlerts = async () => {
    try {
      const r = await axios.get(`${API}/sync/cron-alerts`);
      setAlerts(r.data?.alerts || []);
    } catch {
      setAlerts([]);
    }
    if (isSuperAdmin) {
      try {
        const rf = await axios.get(`${API}/freshness/alerts`);
        setFreshnessAlerts(rf.data?.alerts || []);
      } catch {
        setFreshnessAlerts([]);
      }
    }
  };

  useEffect(() => {
    fetchAlerts();
    intervalRef.current = setInterval(fetchAlerts, 60_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isSuperAdmin]);

  const totalCount = alerts.length + freshnessAlerts.length;
  if (totalCount === 0) return null;

  const goToJob = (jobId) => {
    setOpen(false);
    navigate(`/sync-center?tab=schedules&highlight=${encodeURIComponent(jobId)}`);
  };

  const acknowledgeAlert = async (jobId, e) => {
    // Prevent the row click (navigate) from firing when the user hits Clear
    e.stopPropagation();
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/sync/cron-alerts/${encodeURIComponent(jobId)}/acknowledge`);
      toast.success(`Alert cleared (${r.data?.records_updated || 0} record(s))`);
      setAlerts(prev => prev.filter(a => a.job_id !== jobId));
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge alert');
    }
  };

  const dismissFreshnessAlert = async (locationId, date, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await axios.post(`${API}/freshness/alerts/acknowledge`, { location_id: locationId, date });
      toast.success('Freshness alert dismissed — it will re-fire on the next mismatch');
      setFreshnessAlerts(prev => prev.filter(a => !(a.location_id === locationId && a.date === date)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dismiss alert');
    }
  };

  const dismissAllFreshness = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/freshness/alerts/acknowledge-all`);
      toast.success(`Cleared ${r.data?.records_updated || freshnessAlerts.length} freshness alert(s)`);
      setFreshnessAlerts([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear all');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="cron-failure-alert-btn"
          title={`${alerts.length} critical cron job(s) failing — click for details`}
          className="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-red-500" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
          </span>
          <AlertOctagon className="w-3.5 h-3.5" />
          <span>{totalCount} alert{totalCount === 1 ? '' : 's'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        data-testid="cron-failure-alert-popover"
      >
        <div className="p-3 border-b border-slate-100 bg-red-50/50">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4" /> Critical alerts
          </p>
          <p className="text-[11px] text-slate-600 mt-0.5">
            Cron failures + (super admin only) daily data-freshness mismatches.
          </p>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {isSuperAdmin && freshnessAlerts.length > 0 && (
            <div className="border-b border-slate-200 bg-rose-50/40 p-2">
              <div className="flex items-center justify-between px-1 mb-1">
                <p className="text-[11px] font-semibold text-rose-900 flex items-center gap-1">
                  <ShieldAlert className="w-3.5 h-3.5" />
                  Data Freshness ({freshnessAlerts.length})
                </p>
                {freshnessAlerts.length > 1 && (
                  <button
                    type="button"
                    onClick={dismissAllFreshness}
                    data-testid="freshness-dismiss-all-btn"
                    className="text-[10px] text-rose-700 hover:text-rose-900 hover:underline"
                    title="Dismiss all — they'll re-fire on the next verify run if still red"
                  >
                    Dismiss all
                  </button>
                )}
              </div>
              {freshnessAlerts.slice(0, 8).map((a) => (
                <div
                  key={`fresh-${a.location_id}-${a.date}`}
                  data-testid={`freshness-alert-row-${a.location_id}-${a.date}`}
                  className="w-full flex items-center justify-between text-left px-2 py-1.5 hover:bg-rose-100/50 rounded gap-2"
                >
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate('/sync-center?tab=freshness'); }}
                    className="flex-1 flex items-center justify-between text-left gap-2 min-w-0"
                  >
                    <span className="text-xs text-slate-800 truncate">
                      <b>{a.location_name}</b> · {a.date}
                    </span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 shrink-0">
                      {(a.invoices?.status === 'red' ? 'inv ' : '') +
                       (a.appointments?.status === 'red' ? 'appt ' : '') +
                       (a.patients?.status === 'red' ? 'pt' : '')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => dismissFreshnessAlert(a.location_id, a.date, e)}
                    data-testid={`freshness-dismiss-${a.location_id}-${a.date}`}
                    title="Dismiss this alert — it'll re-fire if the next verify still detects a mismatch"
                    className="shrink-0 rounded p-1 hover:bg-rose-200 text-rose-500 hover:text-rose-700 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {alerts.map((a) => (
            <div
              key={a.job_id}
              data-testid={`cron-alert-row-${a.job_id}`}
              className="w-full border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition flex items-start gap-2 p-3"
            >
              <button
                type="button"
                onClick={() => goToJob(a.job_id)}
                className="flex-1 min-w-0 text-left flex items-start gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-slate-800 text-sm truncate">{a.label}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 shrink-0">
                      {a.reason}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Last attempt {formatRelative(a.last_started_at)}
                    {a.last_success_at && (
                      <> · last success {formatRelative(a.last_success_at)}</>
                    )}
                  </p>
                  {a.last_error && (
                    <p className="text-[10px] text-red-700 mt-1 font-mono truncate" title={a.last_error}>
                      {a.last_error}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              </button>
              <button
                type="button"
                onClick={(e) => acknowledgeAlert(a.job_id, e)}
                data-testid={`cron-alert-clear-${a.job_id}`}
                title="Clear this alert — marks recent failed runs as acknowledged. Alert re-fires on the next failure."
                className="shrink-0 rounded p-1 hover:bg-red-100 text-red-500 hover:text-red-700 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="p-2 border-t border-slate-100 bg-slate-50/50">
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/sync-center?tab=schedules'); }}
            data-testid="cron-alert-view-all"
            className="w-full text-[11px] text-slate-600 hover:text-slate-900 flex items-center justify-center gap-1 py-1"
          >
            View all scheduled jobs <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
